import GameData, { EventType } from './data/GameData';
import GameModel, {
  GameState,
  gameStateToString,
  GameType,
  gameTypeToString,
} from './models/GameModel';
import PlayerModel, { CreatePlayerViewModel } from './models/PlayerModel';
import { Subject } from 'rxjs';
import GameEventModel from './models/GameEventModel';
import CreateGameViewModel from './models/CreateGameViewModel';
import { TUserId } from '../../PartyClientResponse';

export default class ChallengeGameClient {
  private gamePlayerCollection: PlayerModel[] = [];
  private gameEventCollection: GameEventModel[] = [];
  private players: Map<string, PlayerModel> = new Map<string, PlayerModel>();
  private gameDocument!: GameModel;
  private _error = '';
  private unsubscribeStreamGameDocument: () => void;
  private unsubscribeGamePlayerCollection: () => void;
  private unsubscribeGameEventCollection: () => void;
  private gameDetailSubject = new Subject<GameModel>();
  private gamePlayerCollectionSubject = new Subject<PlayerModel[]>();
  private gameEventCollectionSubject = new Subject<GameEventModel[]>();
  private gamePlayerJoinedEventSubject = new Subject<PlayerModel>();
  private errorEventSubject = new Subject<string>();

  constructor(private gameData: GameData, private game: GameModel) {
    this.setGameDocument(game);
    this.setPlayerCollection([]);
    this.setEventCollection([]);
    this.unsubscribeStreamGameDocument = gameData.streamGameDocument(game.id, {
      next: (game: GameModel) => {
        this.setGameDocument(game);
      },
      error: () => this.setError('game-detail-get-error'),
    });
    this.unsubscribeGamePlayerCollection = gameData.streamGamePlayerCollection(game.id, {
      next: (players: PlayerModel[]) => {
        this.setPlayerCollection(players);
      },
      error: () => this.setError('game-player-collection-get-error'),
    });
    this.unsubscribeGameEventCollection = gameData.streamGameEventCollection(game.id, {
      next: (events: GameEventModel[]) => {
        this.setEventCollection(events);
      },
      error: () => this.setError('game-player-collection-get-error'),
    });
  }

  destroy() {
    this.unsubscribeGamePlayerCollection();
    this.unsubscribeStreamGameDocument();
    this.unsubscribeGameEventCollection();
  }

  public get error() {
    return this._error;
  }

  private setError(error: string) {
    this._error = error;
    this.errorEventSubject.next(error);
  }

  private setGameDocument(gameDetails: GameModel) {
    this.gameDocument = gameDetails;
    this.game = this.gameDocument;
    this.gameDetailSubject.next(this.game);
  }

  private setPlayerCollection(players: PlayerModel[]) {
    this.gamePlayerCollection = players;
    this.onGamePlayerCollectionUpdateEvent.next(players);
  }

  private setEventCollection(events: GameEventModel[]) {
    this.gameEventCollection = events;
    this.onGameEventCollectionUpdateEvent.next(events);
  }

  get onGameDetailUpdateEvent() {
    return this.gameDetailSubject;
  }

  get onGamePlayerCollectionUpdateEvent() {
    return this.gamePlayerCollectionSubject;
  }

  get onGameEventCollectionUpdateEvent() {
    return this.gameEventCollectionSubject;
  }

  get onErrorEvent() {
    return this.errorEventSubject;
  }

  // createGame(ownerId: TUserId) {
  //   const gameType: GameType = GameType.CHALLENGE;
  //   const gameState = GameState.NONE;
  //   const game = CreateGameViewModel.fromJson({
  //     state: gameState,
  //     stateTitle: gameStateToString(gameState),
  //     type: gameType,
  //     typeTitle: gameTypeToString(gameType),
  //     ownerId,
  //   });
  //   return this.gameData.createGame(game);
  // }

  onPlayerJoinedEvent() {
    return this.gamePlayerJoinedEventSubject;
  }

  addPlayer(player: CreatePlayerViewModel) {
    this.gameData.createGamePlayer(this.game, player);
  }

  removePlayer(player: PlayerModel) {
    this.gameData.deleteGamePlayer(this.game, player);
  }

  private incrementPoints(game: GameModel, player: PlayerModel, direction: number) {
    this.gameData.incrementPlayerField('points', this.gameDocument, player, direction);
    this.gameData.createGameEvent(this.game, {
      type: EventType.PLAYER_POINT_INCREMENT,
      data: { currentValue: player.points, expectedNextValue: player.points + direction },
    });
  }

  removePoint(player: PlayerModel) {
    const direction = -1;
    this.incrementPoints(this.game, player, direction);
  }

  addPoint(player: PlayerModel) {
    const direction = 1;
    this.incrementPoints(this.game, player, direction);
  }

  async startGame() {
    const previousGameState = this.game.state;
    if (previousGameState === GameState.STARTED) {
      return previousGameState;
    }
    await this.gameData.setGameState(this.game, GameState.STARTED);
    await this.gameData.createGameEvent(this.gameDocument, {
      type: EventType.GAME_START,
      data: { previousState: previousGameState },
    });
    return 'started';
  }

  async setGameDisplayName(displayName: string) {
    await this.gameData.setGameName(this.game, displayName);
  }

  async endGame() {
    const previousGameState = this.game.state;
    if (previousGameState === GameState.STOPPED) {
      return previousGameState;
    }
    let winner: Record<string, any> | null = null;
    this.gamePlayerCollection.forEach((player) => {
      console.log(player.points);
      if (player.points >= (winner?.points || 0)) {
        winner = player.toJson();
      }
    });
    await this.gameData.setGameState(this.game, GameState.STOPPED);
    await this.gameData.createGameEvent(this.gameDocument, {
      type: EventType.GAME_END,
      data: { previousState: previousGameState, winner },
    });
    return GameState.STOPPED;
  }

  getPlayers() {
    return this.players;
  }
}
